import { useState } from 'react'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, History, Lock } from 'lucide-react'
import {
  getModulesConfigGlobalRetrieveQueryKey,
  getV1MeRetrieveQueryOptions,
  useModulesCatalogList,
  useModulesConfigGlobalRetrieve,
  useModulesConfigGlobalSet,
} from '@/api/client'
import type { ModuleAudienceEnum, ModuleCatalogItem, ModuleConfigItem } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/admin-accounts/confirm-dialog'
import {
  SOURCE_LABELS,
  alwaysOnActive,
  alwaysOnDescription,
  changeMeta,
  countsSummary,
} from '@/lib/module-config'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_panel/moduly/')({
  beforeLoad: async ({ context }) => {
    // TR-1200 R6/R12: superadmin edits, admin views read-only, nobody else
    // (teachers must not even see the view).
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'superadmin' && me.data.role !== 'admin') {
      throw redirect({ to: '/' })
    }
  },
  component: ModulesPage,
})

const AUDIENCES: { value: ModuleAudienceEnum; label: string }[] = [
  { value: 'school', label: 'Uczniowie szkolni' },
  { value: 'outside', label: 'Użytkownicy pozaszkolni' },
]

type PendingChange = { module: string; label: string; enabled: boolean }

function ModulesPage() {
  const queryClient = useQueryClient()
  const me = useQuery(getV1MeRetrieveQueryOptions())
  const isSuperadmin = me.data?.status === 200 && me.data.data.role === 'superadmin'

  const [audience, setAudience] = useState<ModuleAudienceEnum>('school')
  const [pending, setPending] = useState<PendingChange | null>(null)
  const [error, setError] = useState<string | null>(null)

  const catalog = useModulesCatalogList()
  const config = useModulesConfigGlobalRetrieve({ audience })
  const setOverride = useModulesConfigGlobalSet()

  const catalogItems = catalog.data?.status === 200 ? catalog.data.data : []
  const configItems: ModuleConfigItem[] =
    config.data?.status === 200 ? config.data.data.modules : []
  const itemsByModule = new Map(configItems.map((item) => [item.module, item]))

  const confirmChange = async () => {
    if (!pending) return
    setError(null)
    const res = await setOverride.mutateAsync({
      module: pending.module,
      data: { enabled: pending.enabled },
      params: { audience },
    })
    if (res.status !== 200) {
      setError('Nie udało się zapisać zmiany. Odśwież widok i spróbuj ponownie.')
      return
    }
    await queryClient.invalidateQueries({ queryKey: getModulesConfigGlobalRetrieveQueryKey() })
    setPending(null)
  }

  return (
    <>
      <PageHeader
        title="Zarządzanie modułami"
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/moduly/historia">
              <History /> Historia zmian
            </Link>
          </Button>
        }
      />
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {AUDIENCES.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={audience === option.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAudience(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          {!isSuperadmin && (
            <StatusBadge tone="zinc">
              <Eye className="size-3" /> Podgląd — zmiany wprowadza superadministrator
            </StatusBadge>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          Konfiguracja globalna obowiązuje wszystkie{' '}
          {audience === 'school' ? 'szkoły, klasy i uczniów' : 'konta pozaszkolne'} bez własnego
          nadpisania. Hierarchie szkolna i pozaszkolna są od siebie niezależne.
        </p>

        <div className="space-y-3">
          {catalogItems.map((entry) =>
            entry.toggleable ? (
              <ToggleableRow
                key={entry.module}
                entry={entry}
                item={itemsByModule.get(entry.module)}
                canEdit={isSuperadmin}
                onChange={(enabled) =>
                  setPending({ module: entry.module, label: entry.label, enabled })
                }
              />
            ) : (
              <LockedRow key={entry.module} entry={entry} audience={audience} />
            ),
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null)
            setError(null)
          }
        }}
        title={
          pending?.enabled
            ? `Włączyć moduł „${pending?.label}"?`
            : `Wyłączyć moduł „${pending?.label}"?`
        }
        description={<ChangeDescription pending={pending} item={itemsByModule} />}
        confirmLabel={pending?.enabled ? 'Włącz moduł' : 'Wyłącz moduł'}
        destructive={pending?.enabled === false}
        pending={setOverride.isPending}
        error={error}
        onConfirm={confirmChange}
      />
    </>
  )
}

function ChangeDescription({
  pending,
  item,
}: {
  pending: PendingChange | null
  item: Map<string, ModuleConfigItem>
}) {
  if (!pending) return null
  const counts = countsSummary(item.get(pending.module)?.lower_override_counts)
  return (
    <span className="space-y-2">
      <span className="block">
        Zmiana obejmie wszystkie obiekty bez własnego nadpisania na niższym poziomie.
        {counts && (
          <>
            {' '}
            Nie obejmie nadpisań: <strong>{counts}</strong>.
          </>
        )}
      </span>
      {!pending.enabled && (
        <span className="block">
          Wyłączenie ukrywa sekcję w aplikacji, ale nie usuwa danych ani postępów uczniów.
        </span>
      )}
    </span>
  )
}

function ToggleableRow({
  entry,
  item,
  canEdit,
  onChange,
}: {
  entry: ModuleCatalogItem
  item: ModuleConfigItem | undefined
  canEdit: boolean
  onChange: (enabled: boolean) => void
}) {
  if (!item) return null
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-4 py-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{entry.label}</span>
            <StatusBadge tone={item.effective_enabled ? 'green' : 'zinc'}>
              {item.effective_enabled ? 'Włączony' : 'Wyłączony'}
            </StatusBadge>
            <StatusBadge tone="outline">{SOURCE_LABELS[item.source]}</StatusBadge>
          </div>
          <p className="text-xs text-muted-foreground">
            Ostatnia zmiana: {changeMeta(item.override) || '—'}
          </p>
        </div>
        {canEdit && (
          <Button
            type="button"
            size="sm"
            variant={item.effective_enabled ? 'outline' : 'default'}
            onClick={() => onChange(!item.effective_enabled)}
          >
            {item.effective_enabled ? 'Wyłącz' : 'Włącz'}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

function LockedRow({
  entry,
  audience,
}: {
  entry: ModuleCatalogItem
  audience: ModuleAudienceEnum
}) {
  const active = alwaysOnActive(entry, audience)
  return (
    <Card className={cn(!active && 'opacity-70')}>
      <CardContent className="flex items-center justify-between gap-4 py-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{entry.label}</span>
            <StatusBadge tone={active ? 'green' : 'zinc'}>
              {active ? 'Włączony' : 'Nieaktywny'}
            </StatusBadge>
          </div>
          <p className="text-xs text-muted-foreground">{alwaysOnDescription(entry, audience)}</p>
        </div>
        <Lock className="size-4 text-muted-foreground" aria-label="Bez możliwości zmiany" />
      </CardContent>
    </Card>
  )
}
